import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    Box,
    Typography,
    Popover,
    Button,
    IconButton,
    Grid,
    Paper,
    InputBase,
    Checkbox,
    Avatar,
    MenuItem,
    Collapse
} from '@mui/material';
import {
    RefreshIcon,
    CalendarTodayOutlinedIcon,
    AddIcon,
    OpenInNewIcon,
    CloseIcon,
    FilterAltOutlinedIcon,
    PersonAddAlt1OutlinedIcon,
    KeyboardArrowDownOutlinedIcon,
    UploadFileOutlinedIcon,
    SearchIcon
} from 'icons/materialIcons';
import TeamWhiteButtonWithIcon from 'team/shared/components/button/TeamWhiteButtonWithIcon';
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    addDays,
    isSameMonth,
    isSameDay,
    addMonths,
    subMonths,
    isWithinInterval
} from 'date-fns';
import WhiteButtonWithIcon from 'components/common/button/WhiteButtonWithIcon';
import DarkButtonWithIcon from 'components/common/button/DarkButtonWithIcon';
import PropTypes from 'prop-types';
import {
    formatDate,
    generateCalendarDays
} from 'utils/dateUtils';
import TeamCreateLeadModal from './addLeads/TeamCreateLeadModal';
import { getTeamSubteams } from 'team/apis/teamSubteamApi';
import { getTeamMembers } from 'team/apis/teamMemberApi';
import { useTeam } from 'team/contexts/teamContext';
import avatarPlaceholder from 'images/avatar-cropped.png';
import TeamCalendarPopover from 'team/shared/components/date/TeamCalendarPopover';

import { getSubteamColor, getSubteamInitials } from 'team/shared/utils/subteamUtils';
import { LEAD_SOURCE_CONFIG } from 'components/contacts/contactMain/contactsTable/leadSourceConfig';

const TeamLeadsMenu = ({
    totalLeads,
    onRefresh,
    onAddLeads,
    onExport,
    leads = [],
    selectedCount = 0,
    onDateRangeChange,
    searchQuery = '',
    onSearchChange,
    onImportCSV,
    isExportRestricted = false,
    isCrmExportRestricted = false,
    filters = { subteams: [], members: [], removedMembers: false, leadType: [] },
    filterSearch = '',
    onFilterChange,
    onFilterSearchChange,
    onClearFilters
}) => {
    const { currentTeamId } = useTeam();
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isCreateLeadModalOpen, setIsCreateLeadModalOpen] = useState(false);
    const exportButtonRef = useRef(null);
    const dateButtonRef = useRef(null);
    const filterButtonRef = useRef(null);

    // State for filter dropdowns
    const [openDropdown, setOpenDropdown] = useState(null);
    const [subteamsList, setSubteamsList] = useState([]);
    const [membersList, setMembersList] = useState([]);
    const [loadingSubteams, setLoadingSubteams] = useState(false);
    const [loadingMembers, setLoadingMembers] = useState(false);
    // Default date range - initially set to all time
    const [dateRange, setDateRange] = useState([
        new Date('2020-01-01'),
        new Date()
    ]);

    // Current calendar view - month being displayed
    const [calendarViewDate, setCalendarViewDate] = useState(new Date());

    // State for date picker popover
    const [datePopoverOpen, setDatePopoverOpen] = useState(false);

    // State for filter popover
    const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
    const [localFilterSearch, setLocalFilterSearch] = useState(filterSearch || '');
    const [localSelectedFilters, setLocalSelectedFilters] = useState({
        subteams: filters?.subteams || [],
        members: filters?.members || [],
        leadType: filters?.leadType || []
    });

    // Sync local state with props when filters change
    useEffect(() => {
        setLocalSelectedFilters({
            subteams: filters?.subteams || [],
            members: filters?.members || [],
            leadType: filters?.leadType || []
        });
    }, [filters]);

    useEffect(() => {
        setLocalFilterSearch(filterSearch || '');
    }, [filterSearch]);
    const [tempDateRange, setTempDateRange] = useState(dateRange);
    const [currentStep, setCurrentStep] = useState(0); // 0 = start date, 1 = end date

    // Predefined date ranges
    const getPredefinedRanges = () => {
        const today = new Date();

        // Helper function to get date N days ago
        const daysAgo = (days) => {
            const date = new Date(today);
            date.setDate(date.getDate() - days);
            return date;
        };

        // Helper function to get first day of month
        const firstDayOfMonth = (monthsAgo = 0) => {
            const date = new Date(today);
            date.setMonth(date.getMonth() - monthsAgo);
            date.setDate(1);
            return date;
        };

        return [
            { label: 'Today', range: [today, today] },
            { label: 'Yesterday', range: [daysAgo(1), daysAgo(1)] },
            { label: 'Last 7 days', range: [daysAgo(6), today] },
            { label: 'Last 30 days', range: [daysAgo(29), today] },
            { label: 'This month', range: [firstDayOfMonth(0), today] },
            { label: 'Last month', range: [firstDayOfMonth(1), firstDayOfMonth(0)] },
            { label: 'This year', range: [new Date(today.getFullYear(), 0, 1), today] },
            { label: 'All time', range: [new Date('2020-01-01'), today] }
        ];
    };

    const predefinedRanges = getPredefinedRanges();

    // Format month and year for display
    const formatMonthYear = (date) => {
        return format(date, 'MMMM yyyy');
    };

    // Format full date for display
    const formatFullDate = (date) => {
        return format(date, 'MMM dd, yyyy');
    };

    // Generate calendar days for the current month view
    const generateCalendarDays = (date) => {
        const monthStart = startOfMonth(date);
        const monthEnd = endOfMonth(date);
        const startDate = startOfWeek(monthStart);
        const endDate = endOfWeek(monthEnd);

        const days = [];
        let day = startDate;

        while (day <= endDate) {
            days.push({
                day: isSameMonth(day, monthStart) ? day.getDate() : null,
                date: isSameMonth(day, monthStart) ? new Date(day) : null,
                isCurrentMonth: isSameMonth(day, monthStart)
            });
            day = addDays(day, 1);
        }

        return days;
    };

    const calendarDays = generateCalendarDays(calendarViewDate);

    // Custom isInRange function since date-fns doesn't have it
    const isInRange = (date, startDate, endDate) => {
        if (!date || !startDate || !endDate) return false;
        return date >= startDate && date <= endDate;
    };

    // Handle date range selection
    const handleDateRangeSelect = (range) => {
        setDateRange(range);
        setTempDateRange(range);
        setDatePopoverOpen(false);
        if (onDateRangeChange && Array.isArray(range) && range.length === 2) {
            onDateRangeChange(range[0], range[1]);
        }
    };

    // Handle applying the selected date range
    const handleApplyDateRange = () => {
        let sortedRange = [...tempDateRange];
        if (tempDateRange[0] > tempDateRange[1]) {
            sortedRange = [tempDateRange[1], tempDateRange[0]];
        }
        setDateRange(sortedRange);
        setDatePopoverOpen(false);
        if (onDateRangeChange && Array.isArray(sortedRange) && sortedRange.length === 2) {
            onDateRangeChange(sortedRange[0], sortedRange[1]);
        }
    };

    // Handle date selection in calendar
    const handleDateSelect = (date) => {
        if (currentStep === 0) {
            // Selecting start date
            setTempDateRange([date, tempDateRange[1]]);
            setCurrentStep(1);
        } else {
            // Selecting end date
            const newRange = [tempDateRange[0], date];
            setTempDateRange(newRange);
            setDateRange(newRange);
            setDatePopoverOpen(false);
            setCurrentStep(0);
            if (onDateRangeChange && Array.isArray(newRange) && newRange.length === 2) {
                onDateRangeChange(newRange[0], newRange[1]);
            }
        }
    };

    // Handle predefined range selection
    const handleSelectPredefinedRange = (range) => {
        setDateRange(range);
        setTempDateRange(range);
        setDatePopoverOpen(false);
        if (onDateRangeChange && Array.isArray(range) && range.length === 2) {
            onDateRangeChange(range[0], range[1]);
        }
    };

    // Fetch subteams when filter popover opens
    useEffect(() => {
        const fetchSubteams = async () => {
            if (!filterPopoverOpen || !currentTeamId) return;
            setLoadingSubteams(true);
            try {
                const response = await getTeamSubteams(currentTeamId);
                if (response && response.success && response.subteams) {
                    setSubteamsList(response.subteams);
                } else if (Array.isArray(response)) {
                    setSubteamsList(response);
                }
            } catch (error) {
                console.error('Error fetching subteams:', error);
            } finally {
                setLoadingSubteams(false);
            }
        };
        fetchSubteams();
    }, [filterPopoverOpen, currentTeamId]);

    // Fetch members when filter popover opens
    useEffect(() => {
        const fetchMembers = async () => {
            if (!filterPopoverOpen || !currentTeamId) return;
            setLoadingMembers(true);
            try {
                const response = await getTeamMembers(currentTeamId, { status: 'active' });
                if (response && response.members) {
                    setMembersList(response.members);
                } else if (Array.isArray(response)) {
                    setMembersList(response);
                }
            } catch (error) {
                console.error('Error fetching members:', error);
            } finally {
                setLoadingMembers(false);
            }
        };
        fetchMembers();
    }, [filterPopoverOpen, currentTeamId]);

    // Filter handlers
    const handleFilterClick = (event) => {
        setFilterPopoverOpen(true);
    };

    const handleFilterClose = () => {
        setFilterPopoverOpen(false);
        setOpenDropdown(null);
    };

    const handleFilterSearchChange = (event) => {
        const value = event.target.value;
        setLocalFilterSearch(value);
        if (onFilterSearchChange) {
            onFilterSearchChange(value);
        }
    };

    const handleFilterOptionClick = (option, event) => {
        event.stopPropagation();
        // Open dropdown for subteams, members, and leadType
        setOpenDropdown(openDropdown === option ? null : option);
    };

    const handleItemToggle = (option, itemId) => {
        const currentSelection = localSelectedFilters[option] || [];
        const isSelected = currentSelection.includes(itemId);
        const newSelection = isSelected
            ? currentSelection.filter(id => id !== itemId)
            : [...currentSelection, itemId];

        setLocalSelectedFilters(prev => ({
            ...prev,
            [option]: newSelection
        }));

        // Update parent filter state
        if (onFilterChange) {
            onFilterChange(option, newSelection);
        }
    };

    const handleClearFilters = () => {
        setLocalSelectedFilters({
            subteams: [],
            members: [],
            leadType: []
        });
        setLocalFilterSearch('');
        setOpenDropdown(null);
        if (onClearFilters) {
            onClearFilters();
        }
    };

    const handleApplyFilters = () => {
        // Apply filters - state is already updated via handleFilterOptionChange
        setFilterPopoverOpen(false);
    };

    // Calendar navigation
    const handlePrevMonth = () => {
        setCalendarViewDate(prev => addMonths(prev, -1));
    };

    const handleNextMonth = () => {
        setCalendarViewDate(prev => addMonths(prev, 1));
    };

    // Get current range display text
    const getRangeDisplayText = () => {
        const dateRangeText = `${formatFullDate(dateRange[0])} - ${formatFullDate(dateRange[1])}`;

        // Check if it's "All Time" (from 2020-01-01 to today)
        if (isSameDay(dateRange[0], new Date('2020-01-01')) && isSameDay(dateRange[1], new Date())) {
            return { label: 'All time', dateRange: dateRangeText };
        }

        // Check if it's "This Year" (from start of current year to today)
        const startOfYear = new Date(new Date().getFullYear(), 0, 1);
        if (isSameDay(dateRange[0], startOfYear) && isSameDay(dateRange[1], new Date())) {
            return { label: 'This Year', dateRange: dateRangeText };
        }

        // Check if it's "Last Month"
        const startOfLastMonth = new Date();
        startOfLastMonth.setMonth(startOfLastMonth.getMonth() - 1);
        startOfLastMonth.setDate(1);
        const endOfLastMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 0);
        if (isSameDay(dateRange[0], startOfLastMonth) && isSameDay(dateRange[1], endOfLastMonth)) {
            return { label: 'Last Month', dateRange: dateRangeText };
        }

        if (isSameDay(dateRange[0], dateRange[1])) {
            return { label: formatFullDate(dateRange[0]), dateRange: '' };
        }
        return { label: '', dateRange: dateRangeText };
    };

    // Export handlers
    const handleExportClick = () => {
        if (isExportRestricted) {
            // Notification will be handled by parent
            onExport && onExport('csv');
            return;
        }
        onExport && onExport('csv');
    };

    // Handle add leads button click - directly open create lead modal
    const handleAddLeadsClick = (e) => {
        setIsCreateLeadModalOpen(true);
    };


    // Handle create lead modal close
    const handleCreateLeadModalClose = () => {
        setIsCreateLeadModalOpen(false);
    };

    // Handle create lead success
    const handleCreateLeadSuccess = () => {
        setIsCreateLeadModalOpen(false);
        onRefresh && onRefresh();
    };

    // Handle notifications
    const handleNotification = (message, severity) => {
        console.log('Notification:', message, severity);
    };

    return (
        <Box sx={{ mb: 1 }}>
            <Grid container spacing={2} alignItems="center">
                {/* Left side - Title, Count, and Buttons */}
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'nowrap' }}>
                        {/* Left side - Title */}
                        <Typography variant="body2" sx={{
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            color: '#4f4f4f',
                            flexShrink: 0
                        }}>
                            Leads <span style={{ color: '#828282', fontSize: '0.875rem', fontWeight: 'normal' }}>({totalLeads})</span>
                        </Typography>

                        {/* Right side - Buttons */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', ml: 'auto' }}>
                            {/* Refresh Button */}
                            <Box>
                                <TeamWhiteButtonWithIcon
                                    icon={RefreshIcon}
                                    text="Refresh"
                                    onClick={onRefresh}
                                    padding="0.5rem 0.875rem"
                                    iconProps={{ fontSize: '0.875rem' }}
                                    sx={{
                                        minWidth: '6.5rem',
                                        border: '0.0625rem solid #e3e3e3',
                                    }}
                                />
                            </Box>

                            {/* Filter Button */}
                            <Box ref={filterButtonRef}>
                                <TeamWhiteButtonWithIcon
                                    icon={FilterAltOutlinedIcon}
                                    text="Filter"
                                    onClick={handleFilterClick}
                                    padding="0.5rem 0.875rem"
                                    iconProps={{ fontSize: '0.875rem' }}
                                    sx={{
                                        minWidth: '6.5rem',
                                        border: '0.0625rem solid #e3e3e3',
                                    }}
                                />
                            </Box>

                            {/* Calendar Filter Button */}
                            <Box>
                                <Button
                                    ref={dateButtonRef}
                                    variant="contained"
                                    startIcon={<CalendarTodayOutlinedIcon sx={{ color: '#828282', fontSize: '0.875rem', '& .MuiSvgIcon-root': { fontSize: '0.875rem' } }} />}
                                    onClick={(e) => {
                                        setDatePopoverOpen(true);
                                        dateButtonRef.current = e.currentTarget;
                                    }}
                                    sx={{
                                        backgroundColor: 'white',
                                        color: '#4f4f4f',
                                        borderRadius: '1.25rem',
                                        border: 'none',
                                        boxShadow: 'none',
                                        textTransform: 'none',
                                        fontSize: '0.7rem',
                                        fontWeight: '600',
                                        padding: '0.5rem 0.875rem',
                                        minWidth: '9.5rem',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        '&:hover': {
                                            backgroundColor: 'white',
                                            boxShadow: 2,
                                        },
                                        '& .MuiButton-startIcon': {
                                            mr: 2,
                                            '& .MuiSvgIcon-root': {
                                                fontSize: '0.875rem !important'
                                            }
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        {getRangeDisplayText().label && (
                                            <Box component="span" sx={{ color: '#000000', fontWeight: '600' }}>
                                                {getRangeDisplayText().label}
                                            </Box>
                                        )}
                                        {getRangeDisplayText().dateRange && (
                                            <Box component="span" sx={{ color: '#828282', fontWeight: '600', fontSize: '0.65rem' }}>
                                                {getRangeDisplayText().dateRange}
                                            </Box>
                                        )}
                                    </Box>
                                </Button>
                            </Box>

                            {/* Add Leads Button */}
                            <Box>
                                <TeamWhiteButtonWithIcon
                                    icon={AddIcon}
                                    text="Add Leads"
                                    onClick={handleAddLeadsClick}
                                    padding="0.5rem 0.875rem"
                                    iconProps={{ fontSize: '0.875rem' }}
                                    sx={{
                                        minWidth: '6.5rem',
                                        border: 'none',
                                    }}
                                />
                            </Box>

                            {/* Import Leads Button */}
                            <Box>
                                <TeamWhiteButtonWithIcon
                                    icon={UploadFileOutlinedIcon}
                                    text="Import Leads"
                                    onClick={onImportCSV}
                                    padding="0.5rem 0.875rem"
                                    iconProps={{ fontSize: '0.875rem' }}
                                    sx={{
                                        minWidth: '6.5rem',
                                        border: 'none',
                                    }}
                                />
                            </Box>

                            {/* Export Button */}
                            <Box>
                                <Button
                                    variant="contained"
                                    startIcon={<PersonAddAlt1OutlinedIcon sx={{ color: '#fff', fontSize: '0.875rem' }} />}
                                    onClick={handleExportClick}
                                    disabled={isExportRestricted}
                                    sx={{
                                        backgroundColor: isExportRestricted ? '#e0e0e0' : '#000',
                                        color: isExportRestricted ? '#999' : '#fff',
                                        borderRadius: '1.25rem',
                                        border: 'none',
                                        boxShadow: 'none',
                                        textTransform: 'none',
                                        fontSize: '0.7rem',
                                        fontWeight: '600',
                                        padding: '0.5rem 0.875rem',
                                        minWidth: '6.5rem',
                                        opacity: isExportRestricted ? 0.6 : 1,
                                        cursor: isExportRestricted ? 'not-allowed' : 'pointer',
                                        '&:hover': {
                                            backgroundColor: isExportRestricted ? '#e0e0e0' : '#000',
                                            boxShadow: isExportRestricted ? 'none' : 2,
                                        },
                                        '& .MuiButton-startIcon': {
                                            mr: 1,
                                        }
                                    }}
                                >
                                    Export
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Grid>

            </Grid>

            {/* Full Width Search Bar - Separate Row */}
            <Box sx={{ mt: 2, mb: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        backgroundColor: 'white',
                        border: '1px solid #e3e3e3',
                        borderRadius: '20px',
                        px: 2,
                        py: 1
                    }}
                >
                    <SearchIcon
                        sx={{
                            color: '#828282',
                            mr: 1,
                            fontSize: '1.125rem'
                        }}
                    />

                    <InputBase
                        placeholder="Search name, email or company"
                        value={searchQuery}
                        onChange={onSearchChange}
                        sx={{
                            flexGrow: 1,
                            fontSize: '0.875rem',
                            color: '#242020',
                            '& .MuiInputBase-input::placeholder': {
                                color: '#828282',
                                opacity: 1
                            }
                        }}
                        fullWidth
                    />

                    {searchQuery && (
                        <IconButton
                            size="small"
                            onClick={() => onSearchChange({ target: { value: '' } })}
                            sx={{
                                color: '#828282',
                                mr: -0.5
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </Box>

            <TeamCalendarPopover
                open={datePopoverOpen}
                anchorEl={dateButtonRef.current}
                onClose={() => setDatePopoverOpen(false)}
                title="Select Date Range"
                mode="range"
                calendarViewDate={calendarViewDate}
                onPrevMonth={handlePrevMonth}
                onNextMonth={handleNextMonth}
                calendarDays={calendarDays}
                onDateSelect={handleDateSelect}
                predefinedRanges={predefinedRanges}
                onSelectPredefinedRange={handleSelectPredefinedRange}
                tempDateRange={tempDateRange}
                onReset={() => handleDateRangeSelect([new Date('2020-01-01'), new Date()])}
                onApply={handleApplyDateRange}
                formatMonthYear={formatMonthYear}
                formatFullDate={formatFullDate}
            />

            {/* Filter Popover */}
            <Popover
                open={filterPopoverOpen}
                onClose={handleFilterClose}
                anchorEl={filterButtonRef.current}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        borderRadius: '12px',
                        minWidth: 300,
                        maxWidth: 400,
                        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
                        border: '1px solid #e0e0e0'
                    }
                }}
            >
                <Box sx={{ p: 2 }}>
                    {/* Search Field - Using options background color with main search shape */}
                    <Box sx={{
                        mb: 2,
                        '& .MuiBox-root': {
                            marginBottom: 0,
                            border: 'none !important',
                            backgroundColor: '#f7f7f7 !important',
                            borderRadius: '20px !important'
                        },
                        '& .MuiInputBase-root': {
                            fontSize: '0.7rem !important',
                            backgroundColor: 'transparent !important',
                            '& .MuiInputBase-input::placeholder': {
                                color: '#A0A0A0 !important',
                                fontWeight: '400 !important',
                                fontSize: '0.7rem !important',
                                opacity: 1
                            }
                        },
                        '& .MuiInputBase-input': {
                            fontSize: '0.7rem !important',
                            backgroundColor: 'transparent !important',
                            '&::placeholder': {
                                color: '#A0A0A0 !important',
                                fontWeight: '400 !important',
                                fontSize: '0.7rem !important',
                                opacity: 1
                            }
                        }
                    }}>
                        <InputBase
                            placeholder="Search team members or members..."
                            value={localFilterSearch}
                            onChange={handleFilterSearchChange}
                            sx={{
                                width: '100%',
                                fontSize: '0.7rem',
                                color: '#4f4f4f',
                                '& .MuiInputBase-input::placeholder': {
                                    color: '#A0A0A0',
                                    opacity: 1
                                }
                            }}
                        />
                    </Box>

                    {/* Filter Options */}
                    <Box sx={{ mb: 2 }}>
                        {[
                            { key: 'subteams', label: 'Subteams' },
                            { key: 'members', label: 'Members' },
                            { key: 'leadType', label: 'Lead type' }
                        ].map((option) => {
                            const selectedCount = Array.isArray(localSelectedFilters[option.key])
                                ? localSelectedFilters[option.key].length
                                : localSelectedFilters[option.key] ? 1 : 0;
                            const isDropdownOpen = openDropdown === option.key;

                            return (
                                <Box key={option.key} sx={{ marginBottom: '0.75rem' }}>
                                    <Box
                                        sx={{
                                            backgroundColor: '#f7f7f7',
                                            borderRadius: '0.25rem',
                                            padding: '0.75rem 1rem',
                                            minHeight: '3rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: '#f0f0f0'
                                            },
                                            '&:focus-within': {
                                                outline: '0.125rem solid #1976d2',
                                                outlineOffset: '-0.125rem'
                                            }
                                        }}
                                        onClick={(e) => handleFilterOptionClick(option.key, e)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{
                                                fontSize: '0.8rem',
                                                fontWeight: 500,
                                                color: '#666666',
                                                lineHeight: 1.5
                                            }}>
                                                {option.label}
                                            </Typography>
                                            {selectedCount > 0 && (
                                                <Typography sx={{
                                                    fontSize: '0.75rem',
                                                    fontWeight: 500,
                                                    color: '#1976d2',
                                                    backgroundColor: '#e3f2fd',
                                                    padding: '2px 8px',
                                                    borderRadius: '12px'
                                                }}>
                                                    {selectedCount}
                                                </Typography>
                                            )}
                                        </Box>
                                        <KeyboardArrowDownOutlinedIcon
                                            sx={{
                                                fontSize: '1.25rem',
                                                color: '#666',
                                                transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s ease-in-out'
                                            }}
                                        />
                                    </Box>

                                    {/* Dropdown Menu for Subteams and Members */}
                                    {(option.key === 'subteams' || option.key === 'members' || option.key === 'leadType') && (
                                        <Collapse in={isDropdownOpen} timeout="auto" unmountOnExit>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    mt: 0.5,
                                                    maxHeight: 300,
                                                    width: '100%',
                                                    borderRadius: '8px',
                                                    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
                                                    border: '1px solid #e0e0e0',
                                                    overflowY: 'auto'
                                                }}
                                            >
                                                {option.key === 'subteams' ? (
                                                    loadingSubteams ? (
                                                        <MenuItem disabled>
                                                            <Typography sx={{ fontSize: '0.8rem', color: '#666' }}>
                                                                Loading...
                                                            </Typography>
                                                        </MenuItem>
                                                    ) : subteamsList.length === 0 ? (
                                                        <MenuItem disabled>
                                                            <Typography sx={{ fontSize: '0.8rem', color: '#666' }}>
                                                                No subteams found
                                                            </Typography>
                                                        </MenuItem>
                                                    ) : (
                                                        subteamsList.map((subteam) => {
                                                            const isSelected = localSelectedFilters.subteams.includes(subteam._id);
                                                            return (
                                                                <MenuItem
                                                                    key={subteam._id}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleItemToggle('subteams', subteam._id);
                                                                    }}
                                                                    sx={{
                                                                        padding: '0.75rem 1rem',
                                                                        '&:hover': {
                                                                            backgroundColor: '#f5f5f5'
                                                                        }
                                                                    }}
                                                                >
                                                                    <Checkbox
                                                                        checked={isSelected}
                                                                        sx={{
                                                                            color: '#e0e0e0',
                                                                            padding: '0',
                                                                            marginRight: '12px',
                                                                            '&.Mui-checked': {
                                                                                color: '#000',
                                                                            },
                                                                            '& .MuiSvgIcon-root': {
                                                                                borderRadius: '0.5rem',
                                                                            },
                                                                        }}
                                                                    />
                                                                    <Avatar
                                                                        src={subteam.logoUrl}
                                                                        alt={subteam.name}
                                                                        sx={{
                                                                            width: 32,
                                                                            height: 32,
                                                                            marginRight: '12px',
                                                                            backgroundColor: subteam.logoUrl ? 'transparent' : getSubteamColor(subteam._id, subteam.name),
                                                                            border: 'none',
                                                                            flexShrink: 0
                                                                        }}
                                                                    >
                                                                        {!subteam.logoUrl && getSubteamInitials(subteam.name)}
                                                                    </Avatar>
                                                                    <Typography sx={{
                                                                        fontSize: '0.875rem',
                                                                        color: '#333',
                                                                        flex: 1
                                                                    }}>
                                                                        {subteam.name || 'Unnamed Subteam'}
                                                                    </Typography>
                                                                </MenuItem>
                                                            );
                                                        })
                                                    )
                                                ) : option.key === 'members' ? (
                                                    loadingMembers ? (
                                                        <MenuItem disabled>
                                                            <Typography sx={{ fontSize: '0.8rem', color: '#666' }}>
                                                                Loading...
                                                            </Typography>
                                                        </MenuItem>
                                                    ) : membersList.length === 0 ? (
                                                        <MenuItem disabled>
                                                            <Typography sx={{ fontSize: '0.8rem', color: '#666' }}>
                                                                No members found
                                                            </Typography>
                                                        </MenuItem>
                                                    ) : (
                                                        membersList.map((member) => {
                                                            const memberId = member._id || member.userId;
                                                            const isSelected = localSelectedFilters.members.includes(memberId);
                                                            const memberName = member.user?.fullName || member.fullName || 'Unknown';
                                                            const memberAvatar = member.defaultCard?.profilePictureUrl || member.user?.profilePictureUrl || member.profilePictureUrl || null;

                                                            return (
                                                                <MenuItem
                                                                    key={memberId}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleItemToggle('members', memberId);
                                                                    }}
                                                                    sx={{
                                                                        padding: '0.75rem 1rem',
                                                                        '&:hover': {
                                                                            backgroundColor: '#f5f5f5'
                                                                        }
                                                                    }}
                                                                >
                                                                    <Checkbox
                                                                        checked={isSelected}
                                                                        sx={{
                                                                            color: '#e0e0e0',
                                                                            padding: '0',
                                                                            marginRight: '12px',
                                                                            '&.Mui-checked': {
                                                                                color: '#000',
                                                                            },
                                                                            '& .MuiSvgIcon-root': {
                                                                                borderRadius: '0.5rem',
                                                                            },
                                                                        }}
                                                                    />
                                                                    <Avatar
                                                                        src={memberAvatar || undefined}
                                                                        sx={{
                                                                            width: 32,
                                                                            height: 32,
                                                                            marginRight: '12px',
                                                                            backgroundColor: '#e0e0e0'
                                                                        }}
                                                                    >
                                                                        {!memberAvatar && memberName.charAt(0).toUpperCase()}
                                                                    </Avatar>
                                                                    <Typography sx={{
                                                                        fontSize: '0.875rem',
                                                                        color: '#333',
                                                                        flex: 1
                                                                    }}>
                                                                        {memberName}
                                                                    </Typography>
                                                                </MenuItem>
                                                            );
                                                        })
                                                    )
                                                ) : (
                                                    Object.entries(LEAD_SOURCE_CONFIG).map(([key, config]) => {
                                                        const isSelected = localSelectedFilters.leadType.includes(key);
                                                        return (
                                                            <MenuItem
                                                                key={key}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleItemToggle('leadType', key);
                                                                }}
                                                                sx={{
                                                                    padding: '0.75rem 1rem',
                                                                    '&:hover': {
                                                                        backgroundColor: '#f5f5f5'
                                                                    }
                                                                }}
                                                            >
                                                                <Checkbox
                                                                    checked={isSelected}
                                                                    sx={{
                                                                        color: '#e0e0e0',
                                                                        padding: '0',
                                                                        marginRight: '12px',
                                                                        '&.Mui-checked': {
                                                                            color: '#000',
                                                                        },
                                                                        '& .MuiSvgIcon-root': {
                                                                            borderRadius: '0.5rem',
                                                                        },
                                                                    }}
                                                                />
                                                                <Typography sx={{
                                                                    fontSize: '0.875rem',
                                                                    color: '#333',
                                                                    flex: 1
                                                                }}>
                                                                    {config.label}
                                                                </Typography>
                                                            </MenuItem>
                                                        );
                                                    })
                                                )}
                                            </Paper>
                                        </Collapse>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            onClick={handleClearFilters}
                            sx={{
                                backgroundColor: 'white',
                                color: '#828282',
                                borderRadius: '20px',
                                border: 'none',
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                padding: '6px 16px',
                                '&:hover': {
                                    backgroundColor: 'white',
                                    boxShadow: 2,
                                }
                            }}
                        >
                            Clear filter
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleApplyFilters}
                            sx={{
                                backgroundColor: '#000',
                                color: '#fff',
                                borderRadius: '20px',
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                padding: '6px 16px',
                                '&:hover': {
                                    backgroundColor: '#333'
                                }
                            }}
                        >
                            Apply filter
                        </Button>
                    </Box>
                </Box>
            </Popover>


            {/* Create Lead Modal */}
            <TeamCreateLeadModal
                open={isCreateLeadModalOpen}
                onClose={handleCreateLeadModalClose}
                onSuccess={handleCreateLeadSuccess}
                onNotification={handleNotification}
            />

        </Box>
    );
};

TeamLeadsMenu.propTypes = {
    totalLeads: PropTypes.number.isRequired,
    onRefresh: PropTypes.func.isRequired,
    onAddLeads: PropTypes.func.isRequired,
    onExport: PropTypes.func,
    leads: PropTypes.array,
    selectedCount: PropTypes.number,
    onDateRangeChange: PropTypes.func,
    searchQuery: PropTypes.string,
    onSearchChange: PropTypes.func,
    onImportCSV: PropTypes.func
};

export default TeamLeadsMenu;